import { useState, useRef, useEffect } from "react";

import { chosenSites } from "app/common/services/chosenSites";
import ColorStack from "app/components/colorsStack/ColorStack";
import { CHART_COLORS } from "app/constants/ChartColors";
import { hasPartialWeek } from "app/common/keyword-research/keywords-overview/trendOverTimeTimeframeHelpers";
import type { IDurationData } from "app/services/DurationService";
import { BasicDurations } from "app/services/DurationService";
import type { ValueOf } from "pro-features/types";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import type { ITransformedDataPoint } from "../table/clicks-over-time-graph/types";

import { useClicksOverTimeData } from "./useClicksOverTimeData";
import { calculateTotals } from "../helpers/calculateTotals";

const DEFAULT_SELECTED_DOMAINS_COUNT = 5;

export const useClicksOverTimeGraphData = (
    keywords: string[],
    graphGranularity: GranularityNames,
    graphDuration: ValueOf<typeof BasicDurations>,
    durationData: IDurationData,
) => {
    // 1. Fetch and transform backend data (immutable)
    const {
        data: clicksOverTimeData,
        isLoading,
        error,
    } = useClicksOverTimeData(keywords, graphGranularity, graphDuration);
    const containsPartialWeek = hasPartialWeek(
        graphGranularity,
        graphDuration === BasicDurations.LAST_TWENTY_EIGHT_DAYS,
        durationData,
    );

    // 2. Visual state: which series are visible, color stack, etc.
    const [clicksOverTimeGraphData, setClicksOverTimeGraphData] = useState<ITransformedDataPoint[]>([]);
    const colorStack = useRef(new ColorStack(CHART_COLORS.chartMainColors));

    // 3. Initialize uiGraphData when clicksOverTimeData changes
    useEffect(() => {
        if (!clicksOverTimeData) {
            return;
        } else if (clicksOverTimeData.length === 0) {
            setClicksOverTimeGraphData([]);
            return;
        }
        colorStack.current.reset();
        const displayGraphData = clicksOverTimeData.map((series, index) => ({
            ...series,
            visible: index < DEFAULT_SELECTED_DOMAINS_COUNT,
            color: chosenSites.getSiteColor(series.id),
        }));

        // Calculate totals
        const totalData = calculateTotals({
            graphData: displayGraphData,
            containsPartialWeek,
        });
        const displayGraphDataWithTotal =
            Object.keys(totalData).length > 0
                ? [...displayGraphData, totalData as ITransformedDataPoint]
                : displayGraphData;
        setClicksOverTimeGraphData(displayGraphDataWithTotal);
    }, [clicksOverTimeData, graphGranularity, graphDuration, containsPartialWeek]);

    // 4. Handler for legend toggle
    const handleLegendToggle = (visibleSeries: ITransformedDataPoint[]) => {
        if (!clicksOverTimeGraphData.length) {
            return;
        }
        const prevVisibleIds = clicksOverTimeGraphData.filter((series) => series.visible).map((series) => series.id);
        const newVisibleIds = visibleSeries.map((series) => series.id);
        // Find the toggled series
        const hiddenSeriesId = prevVisibleIds.find((id) => !newVisibleIds.includes(id));
        const visibleSeriesId = !hiddenSeriesId ? newVisibleIds.find((id) => !prevVisibleIds.includes(id)) : undefined;
        // Update only the toggled series
        const updated = clicksOverTimeGraphData.map((seriesItem) => {
            if (hiddenSeriesId && seriesItem.id === hiddenSeriesId) {
                return { ...seriesItem, visible: false };
            }
            if (visibleSeriesId && seriesItem.id === visibleSeriesId) {
                return { ...seriesItem, visible: true };
            }
            return seriesItem;
        });
        // Recalculate totals for visible series
        const filteredDataForTotalCalculation = updated.filter((series) => series.visible).slice(0, updated.length - 1);
        if (filteredDataForTotalCalculation.length > 0) {
            const totalData = calculateTotals({
                graphData: filteredDataForTotalCalculation,
                containsPartialWeek,
            });
            // Only slice off the last item if isTotal === true for one of the items
            const hasTotal = updated.some((item) => item.isTotal === true);
            const baseArray = hasTotal ? updated.slice(0, -1) : updated;
            const baseArrayWithTotal =
                Object.keys(totalData).length > 0 ? [...baseArray, totalData as ITransformedDataPoint] : baseArray;
            setClicksOverTimeGraphData(baseArrayWithTotal);
        } else {
            setClicksOverTimeGraphData(updated);
        }
    };

    return {
        graphData: clicksOverTimeGraphData,
        handleLegendToggle,
        isLoading,
        error,
    };
};
